import type { Auth0User } from '@nexus/supabase';

import type { ProfilePictureResolver } from './use-profile-picture';

// Auth0 federated login identity for Microsoft.
const MS_LOGIN_CONNECTION = 'microsoft-graph';
const GRAPH_PHOTO_URL = 'https://graph.microsoft.com/v1.0/me/photo/$value';

/**
 * Build a {@link ProfilePictureResolver} that resolves the signed-in user's
 * Microsoft profile photo as a `data:` URL.
 *
 * `getToken` is the app-owned Microsoft Graph token-getter (apps/ari and
 * apps/ari-cloud acquire it differently). The resolver fetches Graph
 * `/me/photo/$value` and converts the blob to a `data:` URL — not `blob:`,
 * so the URL is GC'd with the string instead of pinning the Blob in the URL
 * registry across React Query's cache TTL.
 *
 * Returns null when the user is not a Microsoft login, when `getToken`
 * yields null, or when Graph cannot return a photo (404/401/network).
 * Never throws — `useProfilePicture` falls back to `user.picture` on null.
 */
export function createMicrosoftPhotoResolver(getToken: () => Promise<string | null>): ProfilePictureResolver {
  return async (user: Auth0User): Promise<string | null> => {
    if (user.connection !== MS_LOGIN_CONNECTION) return null;

    try {
      const token = await getToken();
      if (!token) return null;

      const res = await fetch(GRAPH_PHOTO_URL, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return null;

      return blobToDataUrl(await res.blob());
    } catch {
      return null;
    }
  };
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}
